import React from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import { SidebarProps } from '../../types';
import { SidebarDivider } from '../atoms';
import { SidebarMenuGroup, SidebarUserProfile, SidebarWorkspaceCard } from '../molecules';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';

/**
 * ============================================
 * SIDEBAR - ORGANISM
 * ============================================
 * Componente principal del sidebar que integra todos los
 * subcomponentes: logo, workspace card, menu groups y user profile.
 *
 * Sigue principios de Atomic Design y Screaming Architecture.
 *
 * @example
 * ```tsx
 * <Sidebar
 *   logo="/logo.png"
 *   workspace={{
 *     title: "Mesa de trabajo",
 *     projectId: "AR-0289-PRUEBA",
 *     clientName: "ARON ALVAREZ",
 *     status: { label: "Ganada", color: "success" }
 *   }}
 *   menuGroups={[
 *     {
 *       title: "CAMPAÑAS",
 *       items: [
 *         { id: "map", label: "Mapa", icon: faEarthAmericas },
 *         { id: "list", label: "Lista de campañas", icon: faList }
 *       ]
 *     }
 *   ]}
 *   userProfile={{
 *     name: "Maria Ayala",
 *     role: "Administrador",
 *     avatarProps: { type: "people", src: "/avatar.jpg" }
 *   }}
 * />
 * ```
 */
export const Sidebar: React.FC<SidebarProps> = ({
  logo,
  onLogoClick,
  workspace,
  menuGroups = [],
  userProfile,
  collapsed = false,
  onToggleCollapse,
  className,
  width = 248,
}) => {
  const sidebarWidth = collapsed ? 64 : width;

  return (
    <aside
      className={cn(
        'ui-imj',
        'ui-flex ui-flex-col ui-justify-between',
        'ui-h-full ui-bg-surface-secondary',
        'ui-border-r-1 ui-border-default',
        'ui-relative ui-p-16 ui-transition-all ui-duration-300',
        className,
      )}
      style={{ width: sidebarWidth }}
    >
      {/* Contenedor principal (top content) */}
      <div className="ui-flex ui-w-full ui-flex-col ui-items-center ui-gap-8 ui-overflow-y-auto">
        {/* Logo */}
        {logo && (
          <div
            className={cn('ui-flex ui-items-center ui-justify-center ui-py-8', 'ui-cursor-pointer')}
            onClick={onLogoClick}
          >
            {typeof logo === 'string' ? (
              <img
                src={logo}
                alt="Logo"
                className="ui-h-40 ui-w-40 ui-rounded-control-pill ui-object-cover"
              />
            ) : (
              logo
            )}
          </div>
        )}

        {/* Divider después del logo */}
        {logo && <SidebarDivider />}

        {/* Workspace Card */}
        {workspace && !collapsed && <SidebarWorkspaceCard {...workspace} />}

        {/* Menu Groups */}
        {menuGroups.map((group, index) => (
          <SidebarMenuGroup key={group.title || `group-${index}`} {...group} />
        ))}
      </div>

      {/* Contenedor inferior (user profile) */}
      {userProfile && !collapsed && <SidebarUserProfile {...userProfile} />}

      {/* Botón de toggle collapse */}
      {onToggleCollapse && (
        <button
          className={cn(
            'ui-absolute ui-flex ui-items-center ui-justify-center',
            'ui-size-32 ui-rounded-control-pill ui-bg-fill-brand',
            'ui-cursor-pointer ui-transition-all ui-duration-200',
            'hover:ui-bg-fill-brand-hover',
            collapsed ? 'ui-right-[-16px]' : '-ui-right-16',
          )}
          style={{ top: 68, right: -16 }}
          onClick={onToggleCollapse}
        >
          <Icon
            name={faChevronLeft}
            size="md"
            color="white"
            className={cn('ui-transition-transform ui-duration-300', collapsed && 'ui-rotate-180')}
          />
        </button>
      )}
    </aside>
  );
};
